/*
 * AIQuality.tsx — S57: AI Quality Metrics Panel
 * API: GET /api/v1/eval/dashboard
 */
import { useState, useEffect } from 'react';
import { API_V1, safeFetch } from '../constants';

interface LatestRun {
  id?: number;
  run_name?: string;
  total_cases?: number;
  cases_passed?: number;
  cases_failed?: number;
  cases_skipped?: number;
  classification_accuracy?: number;
  citation_accuracy?: number;
  mode_accuracy?: number;
  severity_accuracy?: number;
  avg_confidence?: number;
  avg_response_time_ms?: number;
  started_at?: string | null;
  completed_at?: string | null;
}

interface TrendPoint {
  run_id: number;
  date: string;
  classification_accuracy: number;
  citation_accuracy: number;
  total_cases: number;
  cases_passed: number;
}

interface WeakType {
  exception_type: string;
  accuracy?: number;
  pass_rate?: number;
  total_cases?: number;
  passed?: number;
  failed?: number;
}

interface EvalDash {
  latest_run: LatestRun | null;
  trend: TrendPoint[];
  weakest_types: WeakType[];
  total_eval_cases: number;
}

export default function AIQuality() {
  const [data, setData] = useState<EvalDash | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    safeFetch(`${API_V1}/eval/dashboard`, null)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading AI quality metrics...</p></div>;
  if (error) return (
    <div className="main" style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );
  if (!data) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-tertiary)' }}>No eval data available. Run your first evaluation.</p></div>;

  const pct = (v: number | null | undefined) => v != null ? `${(v * 100).toFixed(0)}%` : '0%';
  const bar = (v: number, color: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 120, height: 8, background: 'var(--bg-card)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(v * 100, 100)}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
      <span className="mono" style={{ fontSize: 13 }}>{pct(v)}</span>
    </div>
  );

  // Derive stats from latest_run or defaults
  const run = data.latest_run;
  const totalCases = run?.total_cases ?? data.total_eval_cases ?? 0;
  const passing = run?.cases_passed ?? 0;
  const failing = run?.cases_failed ?? 0;
  const passRate = totalCases > 0 ? passing / totalCases : 0;

  const weakest = (data.weakest_types ?? []).map(w => ({
    type: w.exception_type ?? '',
    rate: w.pass_rate ?? w.accuracy ?? 0,
    total: w.total_cases ?? 0,
    passed: w.passed ?? 0,
  }));

  return (
    <div className="main" style={{ padding: 32, maxWidth: 900 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 24 }}>AI Quality Metrics</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'Total Cases', value: totalCases, color: 'var(--text-primary)' },
          { label: 'Passing', value: passing, color: 'var(--green)' },
          { label: 'Failing', value: failing, color: 'var(--red)' },
          { label: 'Pass Rate', value: pct(passRate), color: passRate >= 0.8 ? 'var(--green)' : 'var(--amber)' },
        ].map((m, i) => (
          <div key={i} className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div className="stitle" style={{ marginBottom: 4 }}>{m.label}</div>
            <div className="mono" style={{ fontSize: 24, fontWeight: 700, color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Accuracy breakdown from latest run */}
      {run && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <h3 className="stitle" style={{ marginBottom: 16 }}>Accuracy Breakdown</h3>
          {[
            { label: 'Classification Accuracy', value: run.classification_accuracy },
            { label: 'Citation Accuracy', value: run.citation_accuracy },
            { label: 'Mode Accuracy', value: run.mode_accuracy },
            { label: 'Severity Accuracy', value: run.severity_accuracy },
          ].filter(a => a.value != null).map(a => (
            <div key={a.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: 13, flex: 1 }}>{a.label}</span>
              {bar(a.value! / 100, a.value! >= 80 ? 'var(--green)' : a.value! >= 50 ? 'var(--amber)' : 'var(--red)')}
            </div>
          ))}
        </div>
      )}

      {/* Trend history */}
      {(data.trend ?? []).length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <h3 className="stitle" style={{ marginBottom: 16 }}>Recent Runs</h3>
          {data.trend.map((t, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: 13 }}>{new Date(t.date).toLocaleDateString()}</span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.cases_passed}/{t.total_cases}</span>
              {bar(t.total_cases > 0 ? t.cases_passed / t.total_cases : 0, 'var(--green)')}
            </div>
          ))}
        </div>
      )}

      {weakest.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <h3 className="stitle" style={{ marginBottom: 12 }}>Weakest Types</h3>
          {weakest.map((w, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ fontSize: 13, color: 'var(--red)' }}>{w.type.replace(/_/g, ' ')}</span>
              <span className="mono" style={{ fontSize: 13 }}>{w.total > 0 ? `${w.passed}/${w.total}` : pct(w.rate)}</span>
            </div>
          ))}
        </div>
      )}

      {!run && (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-tertiary)' }}>
            No evaluation runs completed yet. {data.total_eval_cases > 0 ? `${data.total_eval_cases} eval cases ready.` : ''}
          </p>
        </div>
      )}

      {run?.completed_at && (
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          Last eval run: {new Date(run.completed_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
